import { useSyncConfirm } from 'hic-components'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Exact, OvbResolveType, ReservedResourceInput, ReservedResourceOptions, useReservedResourceUpdateMutation, useUpdateReservedResourcesMutation } from 'src/api/api.generated'
import syncMutation from 'src/utils/sync-mutation'
import { getCoreErrorCodesFromApolloError } from '../error-message/apollo-error-message'
import { UpdateManyReservedResourceInput, UpdateManyReservedResourceOutput, UpdateReservedInput, UpdateReservedResourceOutput } from './update-reserved-resource.types'
import { CoreErrorCode } from '../error-message/error-handler.backend-types'

export type UpdateReservedResourceHookParams = {
  refetchQueries?: string[]
}

const MAX_RETRY_COUNT = 2

export default function useUpdateReservedResourceHook(params?: UpdateReservedResourceHookParams) {
  const { t } = useTranslation()
  const syncConfirm = useSyncConfirm()

  const [pending, setPending] = useState<boolean>(false)

  const [updateReservedResourceMutation, { loading: updateRRMutationLoading }] = useReservedResourceUpdateMutation({
    refetchQueries: [...(params?.refetchQueries ?? []), 'IdentificationDocuments', 'CustomerDocumentImages'],
    awaitRefetchQueries: true,
  })

  const retryHandler = async (overBookingError: boolean, productionError: boolean, displayReservedResource?: string): Promise<boolean> => {
    const title = t('pms.booking.update_reserved_resource.confirm_title')

    const messages: string[] = []
    if (displayReservedResource) {
      messages.push(t('pms.booking.update_reserved_resource.cofirm_message_display_reserved_resource', { display: displayReservedResource }))
    }
    if (overBookingError) {
      messages.push(t('pms.booking.update_reserved_resource.confirm_message_overbooking'))
    }
    if (productionError) {
      messages.push(t('pms.booking.update_reserved_resource.confirm_message_bill_closed'))
    }
    messages.push(t('pms.booking.update_reserved_resource.confirm_message_confirm'))

    const message = messages.join('\n')

    return await syncConfirm({
      title,
      message,
      confirmButtonText: t('commons.ok'),
      cancelButtonText: t('commons.cancel'),
    })
  }

  const updateReservedResource: (input: UpdateReservedInput, retryCount?: number) => Promise<UpdateReservedResourceOutput> = useCallback(
    async (input: UpdateReservedInput, retryCount?: number) => {
      if (!retryCount || retryCount <= MAX_RETRY_COUNT) {
        const variables1: Exact<{
          reservedResourceId: string
          input: ReservedResourceInput
          options: ReservedResourceOptions
        }> = {
          reservedResourceId: input.reservedResourceId,
          input: input.input,
          options: {
            overbooking: input.options?.overbooking ?? OvbResolveType.ERROR,
            production: input.options?.production,
          },
        }

        setPending(true)
        const result1 = await syncMutation(updateReservedResourceMutation, {
          variables: variables1,
        })
        setPending(false)

        const reservedResource1 = result1.result?.data?.updateReservedResource
        const output1: UpdateReservedResourceOutput =
          reservedResource1 && !result1.error //
            ? { result: 'SUCCESS', reservedResource: reservedResource1 }
            : { result: 'ERROR', error: result1?.apolloError ?? result1.error }
        if (output1.result === 'SUCCESS' || (input.options && !retryCount) || (retryCount ?? 1) + 1 > MAX_RETRY_COUNT) return output1

        const errorCodes = result1.apolloError ? getCoreErrorCodesFromApolloError(result1.apolloError) : []
        const overbookingError = errorCodes.includes('OVERBOOKING_NOT_ALLOWED')
        const productionError = errorCodes.includes('CLOSED_BILL')

        if (!overbookingError && !productionError) return output1

        const confirmed = await retryHandler(overbookingError, productionError, input.displayReservedResource)
        if (!confirmed) return { result: 'CANCELED' }

        return await updateReservedResource(
          {
            ...input,
            options: {
              overbooking: overbookingError ? OvbResolveType.ALLOW : input.options?.overbooking ?? OvbResolveType.ERROR,
              production: productionError ? 'SKIP' : input.options?.production ?? 'SYNC',
            },
          },
          retryCount ? retryCount + 1 : 1,
        )
      }
      return { result: 'ERROR', error: 'MAX_RETRY' }
    },
    [updateReservedResourceMutation],
  )

  const [updateManyReservedResourceMutation, { loading: updateManyRRMutationLoading }] = useUpdateReservedResourcesMutation({
    refetchQueries: [...(params?.refetchQueries ?? [])],
    awaitRefetchQueries: true,
  })

  const updateManyReservedResource: (input: UpdateManyReservedResourceInput) => Promise<UpdateManyReservedResourceOutput> = useCallback(
    async (input: UpdateManyReservedResourceInput) => {
      setPending(true)
      const result = await syncMutation(updateManyReservedResourceMutation, {
        variables: {
          value: input.input,
          options: {
            overbooking: input.options?.overbooking ?? OvbResolveType.ERROR,
          },
        },
      })
      setPending(false)

      const reservedResources = result.result?.data?.updateReservedResources
      const output: UpdateManyReservedResourceOutput =
        reservedResources && !result.error //
          ? { result: 'SUCCESS', reservedResources: reservedResources }
          : { result: 'ERROR', error: result.apolloError ?? result.error }

      if (output.result === 'SUCCESS' || input.options) return output

      const errorCodes = result.apolloError ? getCoreErrorCodesFromApolloError(result.apolloError) : []
      const overbookingError = errorCodes.includes(CoreErrorCode.OVERBOOKING_NOT_ALLOWED)

      if (!overbookingError) return output

      const confirmed = await retryHandler(overbookingError, false)
      if (!confirmed) {
        return { result: 'CANCELED' }
      }

      // Retry allowing overbooking or with skip production flag
      const resultRetry = await syncMutation(updateManyReservedResourceMutation, {
        variables: {
          value: input.input,
          options: {
            overbooking: overbookingError ? OvbResolveType.ALLOW : OvbResolveType.ERROR,
          },
        },
      })

      const reservedResourcesRetry = resultRetry.result?.data?.updateReservedResources
      const outputRetry: UpdateManyReservedResourceOutput =
        reservedResourcesRetry && !resultRetry.error //
          ? { result: 'SUCCESS', reservedResources: reservedResourcesRetry }
          : { result: 'ERROR', error: resultRetry?.apolloError ?? resultRetry.error }

      return outputRetry
    },
    [updateManyReservedResourceMutation, retryHandler],
  )

  return {
    updateReservedResourceMutation: updateReservedResource,
    updateManyReservedResourceMutation: updateManyReservedResource,
    updateRRMutationLoading: pending || updateRRMutationLoading || updateManyRRMutationLoading,
  }
}
